import { Box, Button, Stack, SvgIcon, Typography } from "@mui/material";
import { ReactComponent as EmptyIcon } from "../assets/IconEmptyDark.svg";

/**
 * Low-key empty state for a page section: the empty-box illustration flattened
 * to one muted tone, a message, and an optional action.
 *
 * The illustration's own colours (white/dark body, green, purple, red) pull the
 * eye, so every shape takes the secondary colour — barely off the page
 * background. The small red detail is drawn over the green shape, so it takes
 * the page colour instead and still reads as a cut-out rather than vanishing.
 * Overriding every fill means the one SVG works in both themes.
 *
 * `stacked`: illustration above a centred message, for an empty state standing
 * in for a whole page rather than one section.
 */
export default function EmptySectionState({
  message,
  actionLabel,
  onAction,
  stacked = false,
}) {
  return (
    <Stack
      direction={stacked ? "column" : "row"}
      alignItems="center"
      spacing={stacked ? 1 : 2}
      py={1.5}
      textAlign={stacked ? "center" : undefined}
    >
      <SvgIcon
        component={EmptyIcon}
        inheritViewBox
        aria-hidden
        sx={(theme) => {
          // A whole-page one has the room to be much bigger on desktop
          const size = stacked ? { xs: 72, md: 200 } : 72;
          return {
            width: size,
            height: size,
            flexShrink: 0,
            color: theme.palette.secondary.main,
            "& path[fill]": { fill: "currentColor" },
            '& path[fill="#ff0000"]': {
              fill: theme.palette.background.default,
            },
          };
        }}
      />
      <Box>
        {/* Balanced so a two-line message doesn't leave a lone word below */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textWrap: "balance" }}
        >
          {message}
        </Typography>
        {actionLabel && (
          <Button size="small" onClick={onAction} sx={{ mt: 0.5, ml: -0.75 }}>
            {actionLabel}
          </Button>
        )}
      </Box>
    </Stack>
  );
}
